import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Warehouse, 
  Store, 
  Truck,
  BarChart3,
  ChevronLeft,
  Menu,
  X,
  Wifi,
  WifiOff,
  RefreshCw,
  LogOut,
  Users,
  ClipboardList,
  ClipboardCheck,
  DollarSign,
  Calculator,
  Settings,
  Lock,
  FileText,
  Building
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import NotificationBell from '@/components/NotificationBell';

const NAV_ITEMS = [
  { path: '/nas/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/nas/products', label: 'Products', icon: Package },
  { path: '/nas/shopping', label: 'Shopping', icon: Truck },
  { path: '/nas/pos', label: 'POS', icon: Calculator },
  { path: '/nas/admin', label: 'Admin', icon: Settings },
];

const NASLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);
  
  // Check online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Trigger sync when coming back online
      syncPendingChanges();
    };
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check for pending sync items
    checkPendingSync();
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  const checkPendingSync = () => {
    const pending = JSON.parse(localStorage.getItem('nas_sync_queue') || '[]');
    setPendingSync(pending.length);
  };
  
  const syncPendingChanges = async () => {
    if (!navigator.onLine) return;
    
    const queue = JSON.parse(localStorage.getItem('nas_sync_queue') || '[]');
    if (queue.length === 0) return;
    
    // Process sync queue (will be implemented with backend)
    console.log('Syncing', queue.length, 'pending changes...');
    // After successful sync, clear the queue
    // localStorage.setItem('nas_sync_queue', '[]');
    // setPendingSync(0);
  };
  
  const handleBackToApps = () => {
    localStorage.removeItem('selectedApp');
    navigate('/select-app');
  };
  
  const handleLogout = () => {
    localStorage.removeItem('selectedApp');
    logout();
    navigate('/login');
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 z-40 flex items-center justify-between px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
          <Menu size={20} />
        </Button>
        <div className="flex items-center gap-2">
          <Package size={20} className="text-green-600" />
          <span className="font-semibold text-slate-800">NAS</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            isOnline ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
          </div>
        </div>
      </div>
      
      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-200 flex flex-col",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Sidebar Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-green-600 flex items-center justify-center">
              <Package size={18} className="text-white" />
            </div>
            <div>
              <span className="font-bold text-slate-800">NAS</span>
              <p className="text-[10px] text-slate-500 leading-none">Nenen's Agrivet Supply</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X size={18} />
          </Button>
        </div>
        
        {/* Back to Apps */}
        <div className="p-2 border-b border-slate-100 flex-shrink-0">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-600 hover:text-slate-800"
            onClick={handleBackToApps}
          >
            <ChevronLeft size={16} className="mr-2" />
            Back to Apps
          </Button>
        </div>
        
        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {NAV_ITEMS.filter(item => {
            // Filter admin-only items
            if (item.adminOnly && user?.role !== 'admin') return false;
            // Filter approver-only items (admin, arbe, nicole, pabs, alma)
            if (item.approverOnly) {
              const isAdmin = user?.role === 'admin';
              const approvers = ['arbe', 'nicole', 'pabs', 'alma'];
              const isApprover = approvers.includes(user?.username?.toLowerCase());
              if (!isAdmin && !isApprover) return false;
            }
            return true;
          }).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.path}
                variant={isActive ? "secondary" : "ghost"}
                className={cn(
                  "w-full justify-start",
                  isActive && "bg-green-50 text-green-700 hover:bg-green-100"
                )}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
              >
                <Icon size={18} className="mr-2" />
                {item.label}
              </Button>
            );
          })}
        </nav>
        
        {/* Sync Status */}
        <div className="flex-shrink-0 px-4 py-2 border-t border-slate-100">
          <div className={`flex items-center justify-between p-3 rounded-lg ${
            isOnline ? 'bg-green-50' : 'bg-amber-50'
          }`}>
            <div className="flex items-center gap-2">
              {isOnline ? (
                <Wifi size={16} className="text-green-600" />
              ) : (
                <WifiOff size={16} className="text-amber-600" />
              )}
              <span className={`text-sm font-medium ${isOnline ? 'text-green-700' : 'text-amber-700'}`}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            {pendingSync > 0 && (
              <div className="flex items-center gap-1 text-xs text-amber-600">
                <RefreshCw size={12} />
                {pendingSync} pending
              </div>
            )}
          </div>
        </div>
        
        {/* User & Logout */}
        <div className="flex-shrink-0 p-4 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                <span className="text-sm font-medium text-slate-600">
                  {user?.full_name?.[0] || user?.username?.[0] || 'U'}
                </span>
              </div>
              <div className="text-sm">
                <p className="font-medium text-slate-700 truncate max-w-[120px]">{user?.full_name || user?.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <NotificationBell />
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut size={16} className="text-slate-500" />
              </Button>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default NASLayout;
